import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_GRID_DENSITY,
  readGridDensity,
  writeGridDensity,
  type GridDensity,
} from "./grid-density";

export type GridDensityContextValue = {
  density: GridDensity;
  setDensity: (density: GridDensity) => void;
};

const GridDensityContext = createContext<GridDensityContextValue | null>(null);

export function GridDensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<GridDensity>(() => readGridDensity());

  const setDensity = useCallback((next: GridDensity) => {
    setDensityState(next);
    writeGridDensity(next);
  }, []);

  const value = useMemo(
    () => ({ density, setDensity }),
    [density, setDensity],
  );

  return (
    <GridDensityContext.Provider value={value}>{children}</GridDensityContext.Provider>
  );
}

export function useGridDensity(): GridDensityContextValue {
  const value = useContext(GridDensityContext);
  if (!value) {
    throw new Error("useGridDensity must be used within GridDensityProvider");
  }
  return value;
}

export { DEFAULT_GRID_DENSITY };
